import React from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { importBannerCopy, importFinishedCopy } from "@atlas/shared";
import { useImportStatus } from "../hooks/useImportStatus";
import { invalidateAfterActivityChange } from "../lib/activity-queries";

// ─── Import progress banner ─────────────────────────────────────────────────
// App-wide strip under the top bar while a big import is still landing
// (fetching from Strava, paused on its rate limit, or matching). Without it a
// person with years of walks sees a half-filled record for an hour and fairly
// assumes walks were missed. Mounted once, in AppShell: this instance owns
// the "Import finished" moment and the cache refresh, so it must not be
// mounted twice. Info tokens, not gold: this is status, not achievement.

const FINISHED_VISIBLE_MS = 8_000;
/** While matching, refresh the record at most this often so it fills in visibly. */
const RECORD_REFRESH_MS = 60_000;

export function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// On finish: every view that reads matched activities, plus the dashboard's events.
function refreshRecord(queryClient: QueryClient) {
  invalidateAfterActivityChange(queryClient);
  void queryClient.invalidateQueries({ queryKey: ["events"] });
}

// While matching: only the light record views, never the map layers.
function refreshRecordLight(queryClient: QueryClient) {
  for (const queryKey of [["profile/stats"], ["collections"], ["achievements"]]) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

export function ImportProgressBanner() {
  const queryClient = useQueryClient();
  const { data } = useImportStatus();
  const active = data?.active === true;

  const [showFinished, setShowFinished] = React.useState(false);
  const wasActive = React.useRef(false);
  const lastRefresh = React.useRef(0);

  // Active -> idle seen in this session: say so briefly and refresh once. A
  // cold load that is already idle never shows "finished".
  React.useEffect(() => {
    const was = wasActive.current;
    wasActive.current = active;
    if (active) {
      setShowFinished(false);
      if (lastRefresh.current === 0) lastRefresh.current = Date.now();
    } else if (was) {
      refreshRecord(queryClient);
      lastRefresh.current = 0;
      setShowFinished(true);
    }
  }, [active, queryClient]);

  React.useEffect(() => {
    if (!showFinished) return;
    const t = window.setTimeout(() => setShowFinished(false), FINISHED_VISIBLE_MS);
    return () => window.clearTimeout(t);
  }, [showFinished]);

  // While matching, let the record catch up now and then rather than only at
  // the end, so "fill in as this finishes" is visibly true.
  const matched = data?.matched ?? 0;
  React.useEffect(() => {
    if (!active || matched === 0) return;
    if (Date.now() - lastRefresh.current < RECORD_REFRESH_MS) return;
    lastRefresh.current = Date.now();
    refreshRecordLight(queryClient);
  }, [active, matched, queryClient]);

  if (active && data) {
    const copy = importBannerCopy(data, formatClock);
    if (!copy) return null;
    const total = data.matched + data.pending;
    const showBar = data.phase === "matching" && total > 0;
    return (
      <Strip tone="info" ariaLabel="Import progress">
        <RefreshCw size={15} style={{ color: "var(--status-info)", flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.4 }}>
            {copy.title}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45, marginTop: 2 }}>
            {copy.detail}
          </div>
          {showBar && (
            <div
              role="progressbar"
              aria-label="Walks matched"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={data.matched}
              style={{
                marginTop: 8,
                height: 4,
                maxWidth: 420,
                borderRadius: 999,
                background: "var(--surface-sunken)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.round((data.matched / total) * 100))}%`,
                  height: "100%",
                  background: "var(--status-info)",
                  transition: "width 0.6s ease",
                }}
              />
            </div>
          )}
        </div>
      </Strip>
    );
  }

  if (showFinished) {
    const finished = importFinishedCopy(data);
    return (
      <Strip tone="success" ariaLabel="Import progress" live>
        <CheckCircle2 size={15} style={{ color: "var(--status-success)", flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0, flex: 1, fontSize: 13, lineHeight: 1.45, color: "var(--text-secondary)" }}>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{finished.title}.</span>{" "}
          {finished.detail}
        </div>
      </Strip>
    );
  }

  return null;
}

function Strip({
  tone,
  ariaLabel,
  live = false,
  children,
}: {
  tone: "info" | "success";
  ariaLabel: string;
  /** Announce to screen readers. Off for the progress strip (it changes every poll). */
  live?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      role={live ? "status" : "region"}
      aria-label={ariaLabel}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        flexShrink: 0,
        padding: "10px 16px",
        background: tone === "info" ? "var(--info-soft)" : "var(--success-soft)",
        borderBottom: "1px solid var(--border-subtle)",
        fontFamily: "var(--font-sans)",
      }}
    >
      {children}
    </div>
  );
}
